import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Recaptcha from "@/components/site/Recaptcha";

const ENQUIRY_TYPES = ["General", "Surveillance & AI Analytics", "Command & Control", "Coastal & Maritime", "Cybersecurity & Forensics", "Data Centers & IT", "Smart Governance", "Partnership / OEM"];

const empty = { name: "", organisation: "", designation: "", email: "", phone: "", enquiry_type: "General", message: "", company: "" };

export default function ContactForm() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState("");
  const [captcha, setCaptcha] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Valid email required";
    if (!form.message.trim() || form.message.trim().length < 10) err.message = "Please add a short message (10+ chars)";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/contact", { ...form, recaptcha_token: captcha || "" });
      setDone(true);
      setForm(empty);
    } catch (err) {
      setApiError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div data-testid="contact-success" className="border border-lp-cyan bg-lp-grey rounded-sm p-12 text-center">
        <CheckCircle2 size={48} className="text-lp-cyan mx-auto mb-4" />
        <h3 className="font-heading font-700 text-2xl text-lp-blue">Enquiry Received</h3>
        <p className="text-lp-muted mt-3">Thank you. Our integration team will respond shortly.</p>
        <button onClick={() => setDone(false)} className="mt-6 text-lp-cyan font-600">Send another enquiry</button>
      </div>
    );

  const inputCls = "w-full border border-lp-border rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-lp-cyan focus:ring-1 focus:ring-lp-cyan transition-colors";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="border border-lp-border rounded-sm p-8 bg-white">
      <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} className="hidden" aria-hidden />
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-600 text-lp-ink">Name *</label>
          <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} />
          {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="text-xs font-600 text-lp-ink">Organisation</label>
          <input data-testid="contact-org" value={form.organisation} onChange={set("organisation")} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-600 text-lp-ink">Designation</label>
          <input data-testid="contact-designation" value={form.designation} onChange={set("designation")} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-600 text-lp-ink">Email *</label>
          <input data-testid="contact-email" value={form.email} onChange={set("email")} className={inputCls} />
          {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="text-xs font-600 text-lp-ink">Phone</label>
          <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-600 text-lp-ink">Enquiry Type</label>
          <select data-testid="contact-type" value={form.enquiry_type} onChange={set("enquiry_type")} className={inputCls}>
            {ENQUIRY_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-5">
        <label className="text-xs font-600 text-lp-ink">Message *</label>
        <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className={inputCls} />
        {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
      </div>
      {apiError && <p className="text-sm text-destructive mt-4">{apiError}</p>}
      <Recaptcha onChange={setCaptcha} />
      <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-7 py-3.5 rounded-sm hover:bg-lp-steel transition-colors disabled:opacity-60">
        {sending ? "Sending…" : "Submit Enquiry"} <Send size={16} />
      </button>
    </form>
  );
}
